import { useEffect, useState } from "react";
import { AlertTriangle, Landmark, CheckCircle2 } from "lucide-react";
import { platformGetBrand, platformPatchBrand } from "../../lib/api";

/**
 * Sprint 2 — Brand Settings panel.
 *
 * Admin-only. Tenant slug/id is displayed read-only. Only allowlisted brand
 * fields are editable. Every save creates a `brand.update` audit event.
 */
const LOCALES = ["en-GB", "en-US", "en"];
// Small allowlist of common IANA timezones — the server validates the full list.
const COMMON_TIMEZONES = [
  "Europe/London", "Europe/Paris", "Europe/Berlin",
  "America/New_York", "America/Los_Angeles",
  "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney", "UTC",
];

export default function BrandSettingsPanel() {
  const [brand, setBrand] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const b = await platformGetBrand();
      setBrand(b);
      setForm({
        displayName: b.displayName || "",
        operatingName: b.operatingName || "",
        defaultLocale: b.defaultLocale || "en-GB",
        defaultTimezone: b.defaultTimezone || "Europe/London",
        supportContact: {
          email: b.supportContact?.email || "",
          phone: b.supportContact?.phone || "",
        },
      });
    } catch (e) {
      setError(readError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true); setError(null); setSaved(false);
    // Build a diff — only send changed fields so the audit log's
    // `fields_changed` metadata is meaningful.
    const patch = {};
    for (const k of ["displayName","operatingName","defaultLocale","defaultTimezone"]) {
      if ((form[k] ?? "") !== (brand[k] ?? "")) patch[k] = form[k] || null;
    }
    const sc = {
      email: form.supportContact.email || null,
      phone: form.supportContact.phone || null,
    };
    const scOrig = brand.supportContact || { email: null, phone: null };
    if ((sc.email !== scOrig.email) || (sc.phone !== scOrig.phone)) {
      patch.supportContact = sc.email || sc.phone ? sc : null;
    }
    if (Object.keys(patch).length === 0) { setSaving(false); return; }

    try {
      const updated = await platformPatchBrand(patch);
      setBrand(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(readError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-brand-settings">
      <header className="mb-7">
        <p className="ed-overline mb-2 flex items-center gap-2">
          <Landmark size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" />
          System
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Brand Settings</h1>
        <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
          Manage the identity of your tenant on the Platform. Changes are audited.
        </p>
      </header>

      {loading && <p data-testid="brand-loading" className="text-sm text-[var(--ed-ink-muted)]">Loading brand…</p>}

      {error && (
        <div role="alert" data-testid="brand-error" className="mb-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5" />
          <div>
            <p>{error.message}</p>
            {error.correlationId && (
              <p className="mt-1 text-xs font-mono text-red-700/80">correlation: {error.correlationId}</p>
            )}
          </div>
        </div>
      )}

      {saved && (
        <div role="status" data-testid="brand-saved" className="mb-4 p-3 border border-[var(--ed-emerald)] bg-[var(--ed-emerald-tint)] text-sm text-[var(--ed-emerald)] rounded-sm flex items-center gap-2">
          <CheckCircle2 size={14} /> Brand settings saved.
        </div>
      )}

      {!loading && brand && (
        <form onSubmit={save} className="border border-[var(--ed-rule)] rounded-sm bg-white p-5 sm:p-6 max-w-3xl">
          <ReadOnlyRow label="Tenant" value="wls" testid="brand-tenant-slug" />
          <ReadOnlyRow label="Brand ID" value={brand.id} testid="brand-id" />

          <Row label="Display name" testid="brand-display-name" required>
            <input
              data-testid="brand-input-display-name"
              className={inputCls}
              value={form.displayName || ""}
              onChange={(e) => setForm(f => ({...f, displayName: e.target.value}))}
              maxLength={200}
              required
            />
          </Row>
          <Row label="Operating name" testid="brand-operating-name" hint="Optional. Legal or trading name.">
            <input
              data-testid="brand-input-operating-name"
              className={inputCls}
              value={form.operatingName || ""}
              onChange={(e) => setForm(f => ({...f, operatingName: e.target.value}))}
              maxLength={200}
            />
          </Row>
          <Row label="Default locale" testid="brand-locale">
            <select
              data-testid="brand-input-locale"
              className={inputCls}
              value={form.defaultLocale}
              onChange={(e) => setForm(f => ({...f, defaultLocale: e.target.value}))}
            >
              {LOCALES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </Row>
          <Row label="Default timezone" testid="brand-timezone" hint="IANA identifier">
            <select
              data-testid="brand-input-timezone"
              className={inputCls}
              value={form.defaultTimezone}
              onChange={(e) => setForm(f => ({...f, defaultTimezone: e.target.value}))}
            >
              {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </Row>
          <Row label="Support email" testid="brand-support-email">
            <input
              data-testid="brand-input-support-email"
              type="email"
              className={inputCls}
              value={form.supportContact.email || ""}
              onChange={(e) => setForm(f => ({...f, supportContact:{...f.supportContact, email: e.target.value}}))}
              maxLength={200}
            />
          </Row>
          <Row label="Support phone" testid="brand-support-phone">
            <input
              data-testid="brand-input-support-phone"
              className={inputCls}
              value={form.supportContact.phone || ""}
              onChange={(e) => setForm(f => ({...f, supportContact:{...f.supportContact, phone: e.target.value}}))}
              maxLength={32}
            />
          </Row>

          <div className="mt-6 pt-5 border-t border-[var(--ed-rule)] flex items-center justify-between">
            <p className="text-xs text-[var(--ed-ink-muted)]">
              Updated {brand.updatedAt ? new Date(brand.updatedAt).toLocaleString() : "—"}
            </p>
            <button
              data-testid="brand-save"
              type="submit"
              disabled={saving}
              className="text-[0.7rem] uppercase tracking-[0.22em] py-2.5 px-5 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] transition-colors rounded-sm disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const inputCls = "w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm";

function Row({ label, testid, children, hint, required }) {
  return (
    <div className="mb-4 sm:mb-5 last:mb-0">
      <label className="ed-overline text-[0.55rem] mb-1.5 block" data-testid={`${testid}-label`}>
        {label}{required && <span aria-hidden className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[0.7rem] text-[var(--ed-ink-muted)] mt-1">{hint}</p>}
    </div>
  );
}

function ReadOnlyRow({ label, value, testid }) {
  return (
    <div className="mb-4 sm:mb-5">
      <p className="ed-overline text-[0.55rem] mb-1">{label}</p>
      <p className="text-sm font-mono text-[var(--ed-ink-muted)]" data-testid={testid}>{value}</p>
    </div>
  );
}

function readError(e) {
  const body = e?.response?.data;
  return {
    message: body?.message || "The Platform API could not process the request.",
    correlationId: body?.correlation_id || e?.response?.headers?.["x-correlation-id"],
  };
}
